/**
 * T250 — Cancel / Reactivate control for the current subscription.
 *
 * Renders one of three states based on the subscription row:
 *
 * 1. ACTIVE, no cancel scheduled, not a trial
 *    → "Cancel my plan" link → opens modal.
 *
 * 2. ACTIVE, cancel_at_period_end = true
 *    → "Scheduled to end on <date> · Reactivate" pill.
 *
 * 3. Anything else (trial, already CANCELLED, EXPIRED, etc.)
 *    → nothing rendered.
 */
import { useRef, useState } from "react";
import useDialogDismiss from "../../hooks/useDialogDismiss";
import {
  AlertTriangle, Clock, RotateCcw, X,
} from "lucide-react";

import {
  cancelSubscription, reactivateSubscription,
} from "../../api/subscriptionCancel.api";


function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return iso;
  }
}


function CancelModal({ subscription, onClose, onCancelled }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const panelRef = useRef(null);
  const safeClose = busy ? () => {} : (onClose || (() => {}));
  useDialogDismiss({ open: true, onClose: safeClose, ref: panelRef });
  const [error, setError] = useState("");

  const endDate = formatDate(subscription.current_period_end);
  const planName = subscription.plan?.name || subscription.plan_code;

  async function submit() {
    setBusy(true);
    setError("");
    try {
      const { data } = await cancelSubscription(subscription.id, reason);
      onCancelled(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to cancel. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Cancel subscription"
        className="max-w-md w-full rounded-lg bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-bold text-gray-900">Cancel your plan?</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="text-sm text-gray-700 space-y-2 mb-4">
          <p>You're cancelling <strong>{planName}</strong>.</p>
          <ul className="list-disc pl-5 space-y-1 text-gray-600">
            <li>
              Your <strong>payment mandate is revoked immediately</strong> —
              autopay stops and no further charges will be attempted.
            </li>
            <li>
              Your service stays active until <strong>{endDate}</strong>.
            </li>
            <li>After that date, your account moves to the free tier.</li>
            <li>
              To continue past {endDate}, you'll need to subscribe again
              (a fresh mandate will be created).
            </li>
          </ul>
        </div>

        <label className="block mb-3">
          <span className="text-sm font-medium text-gray-700">
            Mind sharing why? (optional)
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3} maxLength={200}
            placeholder="e.g. Took a break from scoring · Tried a competitor · Pricing"
            /* Explicit bg + text colour — the SPA runs in dark mode, so
               inherited text-white on white bg made typed text invisible
               (founder screenshot 2026-07-06). */
            className="mt-1 w-full border rounded px-2 py-1.5 text-sm bg-white text-gray-900 placeholder:text-gray-400"
          />
          <span className="text-xs text-gray-500">{reason.length}/200 · helps us improve</span>
        </label>

        {error && (
          <div className="flex items-start gap-2 p-2 mb-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            {error}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose} disabled={busy}
            /* Explicit text-gray-700 so the label doesn't inherit the
               SPA's dark-mode text-white (invisible on white modal). */
            className="px-4 py-2 border rounded text-sm text-gray-700 hover:bg-gray-100">
            Keep my plan
          </button>
          <button
            onClick={submit} disabled={busy}
            className="inline-flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50">
            {busy ? "Cancelling…" : "Yes, cancel at period end"}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * variant:
 *   - "inline"  (default) — small underlined text link, for tucked-in
 *                           placements like the plan-status header.
 *   - "section"           — full section-level button, for use as a
 *                           dedicated "Manage subscription" area below
 *                           the plans grid.
 */
export default function CancelControl({ subscription, onUpdated, variant = "inline" }) {
  const [showModal, setShowModal] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!subscription) return null;

  // Backend computes `can_cancel` — mirrors services_cancel's rules
  // (mandate-trials ARE cancellable pre-debit; legacy free trials
  // aren't; already-ended states aren't). Falling back to the older
  // is_trial gate means older backends still get the safe behaviour.
  const canCancel = subscription.can_cancel != null
    ? subscription.can_cancel
    : (!subscription.is_trial
        && !["CANCELLED", "EXPIRED"].includes(subscription.status));
  if (!canCancel && !subscription.cancel_at_period_end) return null;

  const scheduled = subscription.cancel_at_period_end;
  const endDate = formatDate(subscription.current_period_end);

  if (scheduled) {
    async function reactivate() {
      setBusy(true);
      try {
        const { data } = await reactivateSubscription(subscription.id);
        if (typeof onUpdated === "function") onUpdated(data);
      } catch (err) {
        // Backend refuses reactivate once the gateway mandate has been
        // revoked (services_cancel.reactivate_subscription). Surface the
        // message so the customer understands why the "Reactivate" pill
        // didn't just work.
        // eslint-disable-next-line no-alert
        alert(
          err?.response?.data?.detail
          || "Reactivate isn't available — the payment mandate was already "
             + "cancelled. To keep your plan past the end date, subscribe again."
        );
      } finally {
        setBusy(false);
      }
    }
    return (
      <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/40 text-amber-100 rounded-full text-sm">
        <Clock className="w-4 h-4" />
        <span>Ends on <strong>{endDate}</strong></span>
        <button
          onClick={reactivate} disabled={busy}
          className="inline-flex items-center gap-1 ml-1 px-2 py-0.5 bg-amber-500/30 hover:bg-amber-500/50 rounded text-xs">
          <RotateCcw className="w-3 h-3" />
          Reactivate
        </button>
      </div>
    );
  }

  const trigger = variant === "section" ? (
    <button
      onClick={() => setShowModal(true)}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-red-500/50 text-red-300 hover:bg-red-500/10 hover:text-red-200 transition-colors text-sm font-medium"
    >
      <X className="w-4 h-4" />
      Cancel subscription
    </button>
  ) : (
    <div className="mt-3">
      <button
        onClick={() => setShowModal(true)}
        className="text-xs text-text-secondary hover:text-red-400 underline underline-offset-2"
      >
        Cancel my plan
      </button>
    </div>
  );

  return (
    <>
      {trigger}
      {showModal && (
        <CancelModal
          subscription={subscription}
          onClose={() => setShowModal(false)}
          onCancelled={(updated) => {
            setShowModal(false);
            if (typeof onUpdated === "function") onUpdated(updated);
          }}
        />
      )}
    </>
  );
}
